/**
 * The list's head (tabs row + toolbar) gets out of the way while the records
 * are read, and comes back the moment the person turns round.
 *
 * - Scrolling DOWN through the records (they move up) folds the head away;
 * - the FIRST scroll back UP brings it back at once, above the records where
 *   it always sits (it is outside the scrolling area, so it is sticky by
 *   construction — nothing is re-positioned);
 * - at the top of the records it is always shown.
 *
 * Listens in the CAPTURE phase on the records' wrapper, so it hears whichever
 * element actually scrolls (the split table's panes, the card grid) without
 * knowing which one that is. Horizontal scrolling is ignored (scrollTop does
 * not move).
 *
 * Folding the head makes the scrolling area taller, and the browser then
 * clamps scrollTop near the end of the list — that clamp looks like an
 * upward scroll. So every fold / unfold is followed by a short quiet window
 * (the animation's length) in which movement only re-bases, and a head is
 * never folded when the records would not still need scrolling without it
 * (which would otherwise flap open and shut).
 */
import { useEffect, useRef, useState, type RefObject } from 'react'

/** Downward travel (px) before the head folds — a nudge never hides it. */
const HIDE_AFTER_PX = 24
/** Upward travel (px) that brings it back — the first deliberate move. */
const SHOW_AFTER_PX = 4
/** Within this distance of the top the head is always shown. */
const TOP_ZONE_PX = 8
/** Extra scrollable room (px), beyond the head's own height, the records must
 *  have before folding is worth it. */
const MIN_SPARE_PX = 64

/** The fold/unfold animation's length (ms) — the screen's transition uses it. */
export const HEAD_ANIMATION_MS = 220

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined'
      && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

export function useScrollAwayHeader(
  scrollRoot: RefObject<HTMLElement | null>,
  head: RefObject<HTMLElement | null>,
  enabled: boolean,
): boolean {
  const [hidden, setHidden] = useState(false)
  const hiddenRef = useRef(false)
  hiddenRef.current = hidden

  useEffect(() => {
    if (!enabled) { setHidden(false); return }
    const root = scrollRoot.current
    if (!root) return
    const lastTop = new WeakMap<EventTarget, number>()
    let travel = 0            // signed run of movement in one direction
    let quietUntil = 0

    const set = (next: boolean) => {
      if (next === hiddenRef.current) return
      hiddenRef.current = next
      travel = 0
      quietUntil = performance.now() + HEAD_ANIMATION_MS + 80
      setHidden(next)
    }

    const onScroll = (e: Event) => {
      const el = e.target
      if (!(el instanceof HTMLElement)) return
      const top = el.scrollTop
      const prev = lastTop.get(el)
      lastTop.set(el, top)
      if (prev === undefined || prev === top) return   // first sight / sideways
      if (top <= TOP_ZONE_PX) { set(false); return }
      if (performance.now() < quietUntil) return       // the fold's own clamp
      const delta = top - prev
      travel = (travel >= 0) === (delta >= 0) ? travel + delta : delta
      if (travel <= -SHOW_AFTER_PX) { set(false); return }
      if (travel >= HIDE_AFTER_PX && !hiddenRef.current) {
        // Typing in the toolbar (search) keeps it in view.
        const h = head.current
        if (!h) return
        if (document.activeElement && h.contains(document.activeElement)) return
        // Measured now, while it is still shown.
        if (el.scrollHeight - el.clientHeight <= h.offsetHeight + MIN_SPARE_PX) return
        set(true)
      }
    }

    root.addEventListener('scroll', onScroll, { capture: true, passive: true })
    return () => root.removeEventListener('scroll', onScroll, { capture: true })
  }, [enabled, scrollRoot, head])

  return enabled && hidden
}
